import { MoneyValue } from '@round/creatorbase-ui';
import { GenericDropdownOption } from 'App.types';
import { creatorbase } from '@round/api';
import * as yup from 'yup';

export type FormState = {
    activePlatforms: creatorbase.Platform[];
    location: GenericDropdownOption<number> | null;
    contact_details: string;
    paypal_email: string;
    content_tags: GenericDropdownOption<number>[] | null;
    notes: string;
} & Record<creatorbase.Platform, FormValues>;

export type FormValues = {
    profileUrl: string;
    cost: MoneyValue | null;
};

export const defaultFormValues: FormValues = {
    profileUrl: '',
    cost: null,
};

export const getFieldName = (platform: creatorbase.Platform, name: keyof FormValues) => `${platform}.${name}`;

const platformFormValidationSchema = yup.object().shape({
    profileUrl: yup.string().required('Profile is required'),
    cost: yup
        .object({
            // A cleared amount comes back as an empty string, which is a missing cost rather than a
            // malformed one.
            value: yup
                .number()
                .transform((value, originalValue) => (originalValue === '' ? undefined : value))
                .min(0)
                .label('Cost'),
            currency: yup.mixed().nullable(),
        })
        .nullable()
        .test('cost-required', 'Cost and currency are required', (cost) => {
            return !!cost?.currency && typeof cost.value === 'number';
        }),
});

export const validationSchema = yup.object({
    activePlatforms: yup.array().of(yup.string()).min(1, 'At least one platform is required'),
    paypal_email: yup.string().email().label('PayPal email'),
    content_tags: yup
        .array()
        .of(yup.object())
        .nullable()
        .min(1, 'At least one tag is required')
        .required('At least one tag is required')
        .label('Tag'),
    location: yup.object().nullable().label('Location'),
    notes: yup.string().label('Notes'),
    contact_details: yup.string().trim().required('Contact details are required').label('Contact details'),

    tiktok: yup.object().when('activePlatforms', {
        is: (active: string[]) => active?.includes('tiktok'),
        then: platformFormValidationSchema,
        otherwise: yup.object().strip(), // removes validation + value
    }),
    youtube: yup.object().when('activePlatforms', {
        is: (active: string[]) => active?.includes('youtube'),
        then: platformFormValidationSchema,
        otherwise: yup.object().strip(),
    }),
    instagram: yup.object().when('activePlatforms', {
        is: (active: string[]) => active?.includes('instagram'),
        then: platformFormValidationSchema,
        otherwise: yup.object().strip(),
    }),
});
